import { formatCurrency } from '@/utils';
import { useState } from 'react';
import { Card, Button } from '@/components/common';
import { PremiumBreakdown } from './PremiumBreakdown';
import { AddonSelector } from './AddonSelector';
import { IDVSlider } from './IDVSlider';
import type { InsurerQuote } from '@/types/integrations';

interface QuoteCardProps {
  quote: InsurerQuote;
  onSelect: (insurerCode: string) => void;
  isLoading?: boolean;
}

function formatINR(amount: number) {
  return formatCurrency(amount);
}

function SkeletonQuoteCard() {
  return (
    <Card className="animate-pulse space-y-4">
      <div className="flex items-center justify-between">
        <div className="h-5 w-32 rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-4 w-16 rounded bg-gray-200 dark:bg-gray-700" />
      </div>
      <div className="h-10 w-40 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="h-24 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="h-10 rounded bg-gray-200 dark:bg-gray-700" />
    </Card>
  );
}

export function QuoteCard({ quote, onSelect, isLoading }: QuoteCardProps) {
  const defaultIdv = quote.idv?.default_idv ?? 0;
  const [idvValue, setIdvValue] = useState(defaultIdv);

  if (isLoading) return <SkeletonQuoteCard />;

  return (
    <Card className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            {quote.cover_label ? `${quote.cover_label} \u2014 ${quote.insurer_name ?? quote.insurer_code}` : quote.insurer_name}
          </h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">{quote.insurer_code}</span>
        </div>
      </div>

      {/* Premium display */}
      <div>
        <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
          {formatINR(quote.premium_net)}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Total: <span className="font-medium text-gray-700 dark:text-gray-300">{formatINR(quote.premium_total)}</span>
          {' '}(incl. tax)
        </p>
      </div>

      {/* IDV Slider */}
      {quote.idv && (
        <IDVSlider idv={quote.idv} value={idvValue} onChange={setIdvValue} />
      )}

      {/* Addons */}
      {quote.addons_available.length > 0 && (
        <AddonSelector addons={quote.addons_available} onChange={() => {}} />
      )}

      {/* Premium breakdown */}
      <PremiumBreakdown
        breakdown={quote.premium_breakdown}
        taxAmount={quote.tax_amount}
        grossPremium={quote.premium_total}
      />

      {/* CTA */}
      <Button
        variant="primary"
        size="lg"
        className="w-full mt-auto"
        onClick={() => onSelect(quote.insurer_code)}
      >
        Select This Plan
      </Button>
    </Card>
  );
}
